import React, { useEffect, useState } from 'react';
import './AdminCorrections.css';

const STATUS_FILTERS = ['pending', 'reviewed', 'rejected', 'all'];

const formatSubmitter = (user) => {
  if (!user) return 'Unknown user';
  return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || 'Unknown user';
};

const AdminCorrections = () => {
  const [corrections, setCorrections] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState('');

  const fetchCorrections = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/admin/corrections?status=${statusFilter}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`
        }
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to load corrections.');
      }

      setCorrections(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCorrections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const updateCorrectionStatus = async (correctionId, status) => {
    setUpdatingId(correctionId);
    setError('');

    try {
      const response = await fetch(`/api/admin/corrections/${correctionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({ status })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to update correction.');
      }

      setCorrections(current => current.filter(correction => correction._id !== correctionId));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId('');
    }
  };

  return (
    <main className="admin-corrections-page">
      <section className="admin-corrections-shell">
        <div className="admin-corrections-header">
          <div>
            <p>Admin</p>
            <h1>Correction Review Queue</h1>
            <span>Review user-submitted correction and support reports. This queue does not automatically change public pricing.</span>
          </div>
          <div className="admin-correction-tabs">
            {STATUS_FILTERS.map(status => (
              <button
                key={status}
                type="button"
                className={statusFilter === status ? 'active' : ''}
                onClick={() => setStatusFilter(status)}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {error && <div className="admin-corrections-error">{error}</div>}
        {loading && <div className="admin-corrections-empty">Loading corrections...</div>}

        {!loading && !corrections.length && (
          <div className="admin-corrections-empty">No {statusFilter === 'all' ? '' : statusFilter} corrections found.</div>
        )}

        <div className="admin-corrections-list">
          {corrections.map(correction => (
            <article key={correction._id} className="admin-correction-card">
              <div className="admin-correction-card-header">
                <div>
                  <span className={`admin-correction-status status-${correction.status}`}>{correction.status}</span>
                  <h2>{correction.field}</h2>
                </div>
                <time>{new Date(correction.createdAt).toLocaleString()}</time>
              </div>

              <div className="admin-correction-meta">
                <span>{correction.targetType}</span>
                {correction.hospitalName && <span>{correction.hospitalName}</span>}
                <span>{formatSubmitter(correction.user)}</span>
              </div>

              <div className="admin-correction-values">
                <div>
                  <span>Current</span>
                  <strong>{correction.currentValue || 'Not listed'}</strong>
                </div>
                <div>
                  <span>Suggested</span>
                  <strong>{correction.suggestedValue}</strong>
                </div>
              </div>

              {correction.note && (
                <div className="admin-correction-note">
                  <span>Note</span>
                  <p>{correction.note}</p>
                </div>
              )}

              <div className="admin-correction-actions">
                {correction.status !== 'reviewed' && (
                  <button
                    type="button"
                    className="secondary-btn"
                    disabled={updatingId === correction._id}
                    onClick={() => updateCorrectionStatus(correction._id, 'reviewed')}
                  >
                    Mark reviewed
                  </button>
                )}
                {correction.status !== 'rejected' && (
                  <button
                    type="button"
                    className="danger-btn"
                    disabled={updatingId === correction._id}
                    onClick={() => updateCorrectionStatus(correction._id, 'rejected')}
                  >
                    Reject
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default AdminCorrections;
